/*
 * 版权所有 (c) 华为技术有限公司 2024
 * Copyright (c) Huawei Technologies Co., Ltd. 2024. All rights reserved.
 */
import { type OnInit, Component, EventEmitter, Injector, Input, Output, ViewChild } from '@angular/core';
import { type AbstractControl, Validators } from '@angular/forms';
import {
    type Command, type HttpResponse, DMValidators, FormControl, FormGroup, FormSlideWindowAbstract, SelectComponent,
    UtilService,
} from '@dm/core';
import { type SelectOption } from '@dm/core/types';
import { FileSystemModeE, NfsPermissionE } from 'app/common/constants';
import { SmartGuiService, ToolService } from 'app/common/services';
import { type FileSystem, type NFSClient, type NFSShare } from 'app/common/types/mo';
import { ProductConfig, SWITCH } from 'app/config';
import { FileSystemEntryE } from 'app/feature/service/file-service/file-system/create-file-system/create-file-system.component';
import { GuideItemE } from 'app/feature/service/file-service/file-system/file-system-guide/file-system-guide.service';
import { type Observable } from 'rxjs';
import { finalize } from 'rxjs/operators';

import { type NFSClientVm, type SmartguiNfsshareReceived } from '../../../share';
import { ClientType, ClientTypeReceived } from '../../nfs-share.enum';
import { NfsShareStrService } from '../../nfs-share-str.service';
import { ANONYMOUS_ID_DEFAULT, ANONYMOUS_ID_RANGE, SecureE } from '../client-nfs-share.const';
import { ClientNfsShareService } from '../client-nfs-share.service';

@Component({
    selector: 'app-common-add-client-nfs-share',
    templateUrl: './add-client-nfs-share.component.html',
    styleUrls: ['./add-client-nfs-share.component.scss'],
})
export class AddClientNfsShareComponent extends FormSlideWindowAbstract implements OnInit {
    /**
     * 前台表格传入true
     */
    @Input() isFrontTable = false;
    /**
     * 前台表格传入的clients，用于校验重复，后端表格下发接口校验
     */
    @Input() clients = [];
    /**
     * 前台表格完成处理之后，emit数据回去
     */
    @Output() callBack: EventEmitter<NFSClient[]> = new EventEmitter();

    /**
     * 后台表格传入的当前创建的client所属的share
     */
    @Input() nfsShare: NFSShare;
    /**
     * 是否支持GUI，默认支持，create as和创审计日志文件系统的copyfrom下不支持。
     */
    @Input() supportSmartGui = true;
    /**
     * 租户ID
     */
    @Input() vstoreId: string;
    @Input() entry: FileSystemEntryE;
    /**
     * 传入的文件系统
     */
    @Input() fileSystem: FileSystem;

    /**
     * 匿名用户ID取值范围
     */
    ANONYMOUS_ID_RANGE = ANONYMOUS_ID_RANGE;
    ProductConfig = ProductConfig;

    /**
     * 获取的智能推荐数据
     */
    nfsSmartguiData: SmartguiNfsshareReceived = {};

    i18n = this.str.i18n;
    SWITCH = SWITCH;
    /**
     * 高级属性的开关
     */
    advanced = false;
    showAdvance = true;
    loading = false;

    vm: FormGroup;
    hostNameControl: FormControl;
    netNameControl: FormControl;
    errorMessage: any;

    /**
     * 参与判断的表单字段，用于判断NFS共享UNIX权限、kerberos相关权限表单项是否存在读写权限
     */
    toJudgeWriteModeShowArr: string[] = ['unixPermission', 'kerberos5Permission', 'kerberos5iPermission', 'kerberos5pPermission'];

    get vmValue(): NFSClientVm {
        return this.vm ? this.vm.value : null;
    }

    ClientType = ClientType;

    FileSystemEntryE = FileSystemEntryE;

    configGuide = null;
    showGuideTip = true;

    /**
     * 权限级别选项，默认是完整的
     */
    permissionLevelOpts: SelectOption[] = this.str.permissionLevelOpts;

    @ViewChild('nfsClientTypeSelect') nfsClientTypeSelect: SelectComponent;

    constructor(
        public str: NfsShareStrService,
        private service: ClientNfsShareService,
        injector: Injector,
        public utilService: UtilService,
        private smartguiService: SmartGuiService,
        private toolService: ToolService,
    ) {
        super(injector);
    }

    ngOnInit(): void {
        super.ngOnInit();
        let auditFsOpenWriteSwitch = false;

        if (this.fileSystem) {
            auditFsOpenWriteSwitch = this.fileSystem.isAuditLogFs && this.fileSystem.auditLogWritable;
        }

        // 审计日志文件系统下的NFS共享添加客户端的权限有限制
        if (this.entry === FileSystemEntryE.AuditLog && !auditFsOpenWriteSwitch) {
            this.permissionLevelOpts = this.str.auditLogFsPermissionLevelOpts;
        }

        this.pageReady = true;
        this.windowVisible = true;

        // 获取租户ID
        this.vstoreId = this.vstoreId || (this.nfsShare ? this.nfsShare.vstoreId : this.vstoreId);

        // 直接从共享添加用户/用户组，，entry为undefined, 需要先查是不是属于审计日志文件系统
        if (this.nfsShare && this.nfsShare.FSID && !this.entry) {
            // 审计日志文件系统、哈希文件系统和内部文件系统创共享要限制权限
            this.service.getFileSystem(this.nfsShare.FSID, this.nfsShare.vstoreId, { mask: true })
                .subscribe((data) => {
                    if (!data) {
                        return;
                    }

                    const isNotSupportWriteAuditFs = data.isAuditLogFs && !data.auditLogWritable;

                    if (isNotSupportWriteAuditFs ||
                        data.SUBTYPE === FileSystemModeE.SMART_MOBILITY_INNER_FILE_SYSTEM ||
                        data.SUBTYPE === FileSystemModeE.HASH) {
                        this.permissionLevelOpts = this.str.auditLogFsPermissionLevelOpts;
                    }
                    this.getNfsSmartguiData();
                });
        } else {
            this.getNfsSmartguiData();
        }

        this.initConfigGuide(ClientType.host);
    }

    initVm(): void {
        // 切换时需要更换name 的control
        this.hostNameControl = new FormControl([], {
            validators: DMValidators.multiInputValidator(true, [
                this.hostNameValidator(),
                this.repeatValidator(),
            ]),
            updateOn: 'blur',
        });
        this.netNameControl = new FormControl([], {
            validators: DMValidators.multiInputValidator(true, [
                this.networkGroupValidator(),
                this.repeatValidator(),
                DMValidators.maxByteLength(254),
            ]),
            updateOn: 'blur',
        });

        this.vm = new FormGroup({
            // 类型，默认主机
            type: new FormControl(ClientType.host, []),
            // unix权限级别，默认只读 0
            unixPermission: new FormControl(NfsPermissionE.onlyRead, []),
            // kerberos权限级别，默认无 5
            kerberos5Permission: new FormControl(NfsPermissionE.none, []),
            kerberos5iPermission: new FormControl(NfsPermissionE.none, []),
            kerberos5pPermission: new FormControl(NfsPermissionE.none, []),
            // 权限限制，默认 no 1
            all_squash: new FormControl('1', []),
            // root权限限制，不给默认值
            root_squash: new FormControl(null, {
                validators: [
                    Validators.required,
                ],
            }),
            // 源端口校验限制，默认 insecure 1
            secure: new FormControl(SecureE.INSECURE, []),
            anonymousUserId: new FormControl(ANONYMOUS_ID_DEFAULT),
        }, { updateOn: 'blur' });

        // 默认显示主机
        this.vm.addControl('hostName', this.hostNameControl);

        this.errorMessage = {
            root_squash: {
                required: this.str.requiredErr(this.i18n.label.root_squash),
            },
            hostName: {
                required: this.str.requiredErr(this.i18n.label.clients),
                hostName: this.i18n.error.hostName,
                nameRepeat: this.str.repeatErr(this.i18n.label.clients),
            },
            netName: {
                required: this.str.requiredErr(this.i18n.label.clients),
                maxByteLength: this.str.rangeByteOneLengthErr(1, 254),
                networkGrupName: this.i18n.error.netName,
                nameRepeat: this.str.repeatErr(this.i18n.label.clients),
            },
        };
    }

    // type 发生改变
    onTypeChange($event): void {
        const type = $event.value;
        this.showGuideTip = true;
        if (type === ClientType.host) {
            // 选择主机，删除网络组
            if (this.vm.get('netName')) {
                this.vm.removeControl('netName');
                this.vm.addControl('hostName', this.hostNameControl);
            }
        } else if (type === ClientType.net) {
            // 选中网络组, 删除主机
            if (this.vm.get('hostName')) {
                this.vm.removeControl('hostName');
                this.vm.addControl('netName', this.netNameControl);
            }
        }

        this.initConfigGuide(type);
    }

    initConfigGuide(type: ClientType): void {
        if (type === ClientType.host) {
            this.configGuide = {
                guideItems: [GuideItemE.LDAP, GuideItemE.NIS, GuideItemE.DNS],
                textConfigs: [
                    {
                        // 如果增加主机名称，请至少配置{0}、{1}和{2}中的一个。如果增加客户端IP，则不需要配置
                        label: this.i18nService.get('service.share_term_addClient_host_configDomain_info1'),
                        mark: [false, false, false],
                    },
                    {
                        // 如果增加主机名称，请至少配置LDAP域、NIS域和DNS中的一个。当前已配置{0}，您可以继续配置{1}或{2}。如果增加客户端IP，则不需要配置。
                        label: this.i18nService.get('service.share_term_addClient_host_configDomain_info2'),
                        mark: [true, false, false],
                    },
                    {
                        // 如果增加主机名称，请至少配置LDAP域、NIS域和DNS中的一个。当前已配置{0}和{1}，您可以继续配置{2}。如果增加客户端IP，则不需要配置。
                        label: this.i18nService.get('service.share_term_addClient_host_configDomain_info3'),
                        mark: [true, true, false],
                    },
                ],
            };
        } else if (type === ClientType.net) {
            this.configGuide = {
                guideItems: [GuideItemE.LDAP, GuideItemE.NIS],
                textConfigs: [
                    {
                        // 请至少配置{0}和{1}中的一个。
                        label: this.i18nService.get('service.share_term_addClient_networkGroup_configDomain_info1'),
                        mark: [false, false],
                    },
                    {
                        // 请至少配置LDAP域和NIS域中的一个。当前已配置{0}，您可以继续配置{1}。
                        label: this.i18nService.get('service.share_term_addClient_networkGroup_configDomain_info2'),
                        mark: [true, false],
                    },
                ],
            };
        }
    }

    buildCmds(): Command[] {
        const cmds: Command[] = [];

        // 前台表格不需要cmds，只用emit数据回去
        // 先收集数据
        const datas = this.collectionClients();
        if (this.isFrontTable) {
            this.callBack.emit(datas);
        } else {
            return this.buildNFSClientCmds(datas);
        }
        return cmds;
    }

    buildNFSClientCmds(datas: NFSClient[]): Command[] {
        const cmds: Command[] = [];
        const params: any = {};
        params.auth = datas;
        params.nfsID = this.nfsShare.ID;
        params.vstoreId = this.nfsShare.vstoreId;

        cmds.push({
            name: this.i18n.title.addNfsPermission,
            action: this.service.addClients(this.nfsShare.SHAREPATH, params),
        });

        return cmds;
    }

    collectionClients(): NFSClient[] {
        const params = this.vmValue;
        const results: NFSClient[] = [];

        // 先存一些不便的变量，节约内存
        const tempClient: NFSClient = {
            ACCESSVAL: params.unixPermission,
            ACCESSKRB5: ProductConfig.isHideKerberos ? undefined : params.kerberos5Permission,
            ACCESSKRB5I: ProductConfig.isHideKerberos ? undefined : params.kerberos5iPermission,
            ACCESSKRB5P: ProductConfig.isHideKerberos ? undefined : params.kerberos5pPermission,
            ALLSQUASH: params.all_squash,
            ROOTSQUASH: params.root_squash,
            SECURE: params.secure,
            ANONYMOUSID: params.anonymousUserId,
        };

        // 主机
        if (params.type === ClientType.host) {
            (params.hostName as string[]).forEach((name: NFSClient['NAME']) => {
                results.push({
                    NAME: name,
                    _type: ClientType.host,
                    ...tempClient,
                });
            });
        } else if (params.type === ClientType.net) { // 网络组
            (params.netName as string[]).forEach((name: NFSClient['NAME']) => {
                results.push({
                    NAME: '@' + name,
                    _type: ClientType.net,
                    ...tempClient,
                });
            });
        }
        return results;
    }

    /**
     * 当选择【主机】时，校验规则
     * 主机名称格式要求如下：
     * 1. 主机名称长度范围为1到255，由字母、数字、“-”、“.”、“_”组成。
     * 2. 只能以字母或数字开头，不能以“_”或“-”结尾。
     * 3. 不能包含连续的“.”，不能包含“_.”、“._”、“-.”或“.-”，不能为纯数字。
     *
     * IP地址格式要求如下：
     * 1. 支持输入单个客户端IP地址、客户端IP地址段或使用“*”表示全部客户端IP地址。
     * 2. 支持输入IPv4、IPv6地址或两者的混合IP地址。
     * 3. IPv4的掩码部分取值范围为1~32，IPv6的掩码部分取值范围为1~128。示例：192.168.0.10或192.168.0.0/24或*。
     */
    hostNameValidator() {
        return (c: AbstractControl): { [key: string]: boolean } | null => {
            if (!c.value) {
                return null;
            }
            // 若当前类型为网络组
            if (this.vm.value.type === ClientType.net) {
                return null;
            }
            const value = c.value;
            let valid = true;

            if (!this.hostNameIpValid(value) && !this.hostNameStrValid(value)) {
                valid = false;
            }

            return valid ? null : { hostName: true };
        };
    }

    // hostname 非ip校验
    hostNameStrValid(value): boolean {
        // 长度1-255
        if (value.length > 255) {
            return false;
        }

        // 不能以字符串'-'开头
        if (value.charAt(0) === '-') {
            return false;
        }

        // 不能有制表符
        const invalidReg = /\t/;
        if (invalidReg.test(value)) {
            return false;
        }

        return true;
    }

    // hostname ip校验,
    hostNameIpValid(value): boolean {
        if (value === '*') {
            return true;
        }
        if (!DMValidators.validateIpv4(value) || !DMValidators.validateIpv6(value)) {
            // 是ip
            return true;
        }

        // 不是ip，校验是否带子网掩码
        if (value.indexOf('/') < 0) {
            // 既不是ip也没有带子网掩码
            return false;
        }
        const strArr = value.split('/');
        if (strArr.length > 2) {
            // 不是正常格式
            return false;
        }
        const validIpv4 = !DMValidators.validateIpv4(strArr[0]);
        const validIpv6 = !DMValidators.validateIpv6(strArr[0]);

        const reg = /^[0-9]*$/;

        // 不全是数字
        if (!strArr[1] || !reg.test(strArr[1])) {
            return false;
        }
        const num = Number(strArr[1]);
        if (validIpv4 && num >= 1 && num <= 32) {
            return true;
        }
        if (validIpv6 && num >= 1 && num <= 128) {
            return true;
        }
        return false;
    }

    /**
     * 网络组校验规则:
     * 长度为1到254位，由字母，数字、“_”，“-”，“.”，以及中文字符组成。
     */
    networkGroupValidator() {
        return (c: AbstractControl): { [key: string]: boolean } | null => {
            if (!c.value) {
                return null;
            }

            // 若当前类型为主机
            if (this.vm.value.type === ClientType.host) {
                return null;
            }
            const value = c.value;
            let valid = true;

            // 正则校验
            const reg =
                /^[a-zA-Z0-9\u4e00-\u9fa5\u3002\uff1f\uff01\uff0c\u3001\uff1b\uff1a\u201c\u201d\u2018\u2019\uff08\uff09\u300a\u300b\u3008\u3009\u3010\u3011\u300e\u300f\u300c\u300d\ufe43\ufe44\u3014\u3015\u2026\u2014\uff5e\ufe4f\uffe5\-._]*$/;
            if (!reg.test(value)) {
                valid = false;
            }

            return valid ? null : { networkGrupName: true };
        };
    }

    // 不能跟列表中已有选项重复,只有前端表格才会校验，因为后端表格clients是个空数组，返回null
    repeatValidator() {
        return (c: AbstractControl): { [key: string]: boolean } | null => {
            if (!c.value) {
                return null;
            }

            // 之前的逻辑是批量校验，现在改成单个了，由于逻辑有点复杂，直接构造成数组去校验
            const valueArr = [c.value];
            for (let i = 0; i < this.clients.length; i++) {
                const clientObj = this.clients[i];

                // 相等或如果去掉@并且类型是net，那就重复
                const nameIsRepeat = valueArr.indexOf(clientObj.NAME) >= 0 ||
                    (valueArr.indexOf(clientObj.NAME.replace('@', '')) >= 0 && clientObj._type === ClientType.net);

                // 如果是相同类型才比较
                if (clientObj._type === this.vm.get('type').value && nameIsRepeat) {
                    return { nameRepeat: true };
                }
            }
            return null;
        };
    }

    /**
     * 获取NFS智能推荐数据
     */
    getNfsSmartguiData(): void {
        if (!this.smartguiService.switch || !this.vstoreId || !this.supportSmartGui) {
            return;
        }

        // 添加忙等效果
        this.showLoading();
        this.service.getSmartguiNFS(this.vstoreId, { requestTimeoutTip: this.i18n.error.smartRecommendDataTimeoutMsg })
            .pipe(finalize(() => this.hideLoading()))
            .subscribe(({ data }) => {
                // 设置推荐的客户端类型
                let currentClientType: string;
                if (data.type === ClientTypeReceived.host) {
                    currentClientType = ClientType.host;
                } else if (data.type === ClientTypeReceived.net) {
                    currentClientType = ClientType.net;
                }
                const clientTypeItem = this.str.clientTypeOpts.find((item) => item.value === currentClientType);
                this.nfsClientTypeSelect.setFixedOption(clientTypeItem);

                // 设置推荐的权限
                const nfsClientPermissionItem = this.permissionLevelOpts.find((item) => item.value === data.accessval);

                // 推荐权限不存在时不执行操作，保持默认权限
                if (nfsClientPermissionItem) {
                    this.vm.get('unixPermission').setValue(nfsClientPermissionItem.value);
                }
            });
    }

    /**
     * 复写exec，创建omtask任务成功不弹浮窗
     */
    exec(): void {
        const cmds = this.buildCmds();
        if (cmds && cmds.length === 1) {
            this.loading = true;
            (cmds[0].action as Observable<HttpResponse>).pipe(finalize(() => {
                this.loading = false;
            })).subscribe(() => {
                this.hide();
            });
        } else {
            this.hide();
        }
    }
}
